import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SurveyPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [surveyDetails, setSurveyDetails] = useState(null);
  
  const [ratings, setRatings] = useState({
    overall_rating: 0,
    clarity_rating: 0,
    helpfulness_rating: 0
  });
  const [comments, setComments] = useState('');
  const [suggestions, setSuggestions] = useState('');

  useEffect(() => {
    fetchSurveyDetails();
  }, [token]);

  const fetchSurveyDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/surveys/${token}`);
      if (response.ok) {
        const data = await response.json();
        setSurveyDetails(data);
      } else {
        const err = await response.json();
        setError(err.detail || 'Survey not found or already submitted');
      }
    } catch (err) {
      setError('Failed to load survey');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (ratings.overall_rating === 0) {
      toast.error('Please provide an overall rating');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/surveys/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          overall_rating: ratings.overall_rating,
          clarity_rating: ratings.clarity_rating || 3,
          helpfulness_rating: ratings.helpfulness_rating || 3,
          comments: comments || null,
          suggestions: suggestions || null
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Thank you for your feedback!');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to submit survey');
      }
    } catch (err) {
      toast.error('Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ label, value, onChange }) => {
    const [hover, setHover] = useState(0);
    
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">{label}</label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => onChange(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              className="p-1 transition-transform hover:scale-110"
            >
              <Star
                className={`h-8 w-8 transition-colors ${
                  star <= (hover || value)
                    ? 'fill-amber-400 text-amber-400'
                    : 'text-slate-300'
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-indigo-100">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-violet-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading survey...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Survey Unavailable</h1>
          <p className="text-slate-600">{error}</p>
          <Button
            onClick={() => navigate('/')}
            className="mt-6 bg-violet-600 hover:bg-violet-700"
          >
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-100 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Thank You!</h1>
          <p className="text-slate-600 mb-6">
            Your feedback has been submitted successfully. We appreciate you taking the time to share your experience.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-indigo-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Session Feedback</h1>
          {surveyDetails && (
            <p className="text-slate-600">
              Hi {surveyDetails.candidate_name}, please share your feedback for your{' '}
              <span className="font-semibold text-violet-600">{surveyDetails.session_name}</span>
            </p>
          )}
        </div>

        {/* Survey Form */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-8">
          {/* Overall Rating */}
          <div className="text-center">
            <StarRating
              label="Overall Experience *"
              value={ratings.overall_rating}
              onChange={(v) => setRatings(prev => ({ ...prev, overall_rating: v }))}
            />
            <p className="text-xs text-slate-500 mt-1">How was your overall experience?</p>
          </div>

          <div className="border-t border-slate-100 pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Clarity Rating */}
            <StarRating
              label="Clarity of Information"
              value={ratings.clarity_rating}
              onChange={(v) => setRatings(prev => ({ ...prev, clarity_rating: v }))}
            />

            {/* Helpfulness Rating */}
            <StarRating
              label="Helpfulness of Session"
              value={ratings.helpfulness_rating}
              onChange={(v) => setRatings(prev => ({ ...prev, helpfulness_rating: v }))}
            />
          </div>

          {/* Comments */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              What went well? (Optional)
            </label>
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Share what you liked about the session..."
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Suggestions */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Any suggestions for improvement? (Optional)
            </label>
            <Textarea
              value={suggestions}
              onChange={(e) => setSuggestions(e.target.value)}
              placeholder="How could we make it better..."
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={submitting || ratings.overall_rating === 0}
            className="w-full h-12 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-xl font-medium"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Submit Feedback
              </>
            )}
          </Button>
        </div>

        {/* Privacy Note */}
        <p className="text-center text-xs text-slate-500 mt-6">
          Your feedback is valuable and helps us improve. Responses are confidential.
        </p>
      </div>
    </div>
  );
};

export default SurveyPage;
